import { Map, Set } from 'immutable';

import { getPropertyType } from '../taxonomy/properties';
import { getSize } from '../iterable/getSize';
import { reduce } from '../iterable/reduce';
import { get } from '../iterable/get';

import { ALL, COMBINATIONS, INFLUENCE, INFLUENCE_ATTRIBUTES, RETAIN, VARIANTS } from '../../constants/variations';
import { AMOUNT, CURRENCY, VALUE } from '../../constants/product';
import { EBAY, ETSY, FAIRE } from '../../constants/channels';
import { ATTRIBUTE } from '../../constants/attributes';
import { EMPTY } from '../../constants';
import { FORM } from '../../constants/forms';

export function updateInfluence({ affects, category, channel, inventory, taxonomyData, variations }) {
  function reduceAffectedValues(result, values, key) {
    return result.set(key, Set(values));
  }

  function hasInternationalPricing(combination) {
    return (
      !!combination.getIn([ATTRIBUTE.RETAIL_PRICE, CURRENCY.AUD]) ||
      !!combination.getIn([ATTRIBUTE.RETAIL_PRICE, CURRENCY.CAD]) ||
      !!combination.getIn([ATTRIBUTE.RETAIL_PRICE, CURRENCY.EUR]) ||
      !!combination.getIn([ATTRIBUTE.RETAIL_PRICE, CURRENCY.GBP]) ||
      !!combination.getIn([ATTRIBUTE.WHOLESALE_PRICE, CURRENCY.AUD]) ||
      !!combination.getIn([ATTRIBUTE.WHOLESALE_PRICE, CURRENCY.CAD]) ||
      !!combination.getIn([ATTRIBUTE.WHOLESALE_PRICE, CURRENCY.EUR]) ||
      !!combination.getIn([ATTRIBUTE.WHOLESALE_PRICE, CURRENCY.GBP])
    );
  }

  switch (channel) {
    case EBAY: {
      const { affected, variantIndex, variantName } = affects;
      const properties = get(['CATEGORIES', category, 'properties'])(taxonomyData) || {};

      if (
        affected in properties &&
        variantName in properties[affected].affectedBy
      ) {
        const affectingVariant = Map({
          affecting: variantIndex + 1,
          values: Set(properties[variantName].options),
          type: getPropertyType(properties[variantName]),
        });

        const affectedVariant = Map({
          affectedBy: variantIndex,
          values: reduce(properties[affected].affectedBy[variantName], EMPTY.MAP, reduceAffectedValues),
          type: getPropertyType(properties[affected]),
        });

        return variations
          .setIn([INFLUENCE, variantIndex], affectingVariant)
          .setIn([INFLUENCE, variantIndex + 1], affectedVariant);
      }

      return variations;
    }

    case FAIRE: {
      const internationalPricing = getSize(variations.get(COMBINATIONS)) > 0
        ? variations.get(COMBINATIONS).some(hasInternationalPricing)
        : inventory
          ? inventory.getIn([ATTRIBUTE.INTERNATIONAL_PRICING, VALUE])
          : false;

      let result = variations.setIn([INFLUENCE, ATTRIBUTE.INTERNATIONAL_PRICING], internationalPricing);

      if (inventory) {
        result = result
          .setIn([INFLUENCE, ATTRIBUTE.CONTINUE_SELLING], inventory.getIn([ATTRIBUTE.CONTINUE_SELLING, VALUE]))
          .setIn([INFLUENCE, ATTRIBUTE.KEEP_ACTIVE], inventory.getIn([ATTRIBUTE.KEEP_ACTIVE, VALUE]));
      }

      return result;
    }

    default: {
      return variations;
    }
  }
}

export function isOnlyInfluence({ attribute, influence }) {
  function reduceAttributes(result, item) {
    return result && (item === attribute || getSize(influence.get(item)) === 0);
  }

  return INFLUENCE_ATTRIBUTES[ETSY].reduce(reduceAttributes, true);
}
export function setAllInfluenced(variations) {
  const variants = variations.get(VARIANTS);
  const variantsSize = getSize(variants);

  function reduceInfluence(result, influence) {
    return result || getSize(influence) === variantsSize;
  }

  const all = variantsSize > 1
    ? variations.get(INFLUENCE).delete(ALL).reduce(reduceInfluence, false)
    : false;

  return variations.setIn([INFLUENCE, ALL], all);
}

export function changeInfluence({ attribute, product, variantIndex }) {
  function setDefaultValuesToCombinations(variations) {
    function getDefaultValue() {
      if (variations.has(RETAIN)) return null;

      switch (attribute) {
        case ATTRIBUTE.PRICE: {
          return getSize(variations.getIn([INFLUENCE, attribute])) > 0
            ? AMOUNT.ZERO_CENT
            : product.getIn([FORM.INVENTORY, attribute, VALUE]);
        }

        case ATTRIBUTE.PROCESSING_PROFILE: {
          return getSize(variations.getIn([INFLUENCE, attribute])) > 0
            ? null
            : product.getIn([FORM.SHIPPING, attribute, VALUE]);
        }

        case ATTRIBUTE.QUANTITY: {
          return getSize(variations.getIn([INFLUENCE, attribute])) > 0
            ? AMOUNT.ONE
            : product.getIn([FORM.INVENTORY, attribute, VALUE]);
        }

        case ATTRIBUTE.SKU: {
          return getSize(variations.getIn([INFLUENCE, attribute])) > 0
            ? ''
            : product.getIn([FORM.INVENTORY, attribute, VALUE]);
        }

        default: {
          return null;
        }
      }
    }

    function mapCombinations(combination) {
      return combination.set(attribute, getDefaultValue());
    }

    return variations.set(COMBINATIONS, variations.get(COMBINATIONS).map(mapCombinations));
  }

  function updateCombinationsWithPreferredValues(variations) {
    const combinations = variations.get(COMBINATIONS);
    const anotherVariantIndex = Number(!variantIndex);

    function reduceCombinations(result, combination) {
      const visibility = combination.get(ATTRIBUTE.VISIBILITY);
      const optionId = combination.getIn(['options', anotherVariantIndex, 'optionId']);

      if (result[optionId] === undefined || (visibility && !result[optionId][ATTRIBUTE.VISIBILITY])) {
        result[optionId] = {
          [VALUE]: combination.get(attribute),
          [ATTRIBUTE.VISIBILITY]: combination.get(ATTRIBUTE.VISIBILITY),
        };
      }

      return result;
    }

    const options = combinations.reduce(reduceCombinations, {});

    function mapCombinations(combination) {
      const optionId = combination.getIn(['options', anotherVariantIndex, 'optionId']);
      const { [VALUE]: value, [ATTRIBUTE.VISIBILITY]: visibility } = options[optionId];
      return combination.set(attribute, value).set(ATTRIBUTE.VISIBILITY, visibility);
    }

    return variations.set(COMBINATIONS, combinations.map(mapCombinations));
  }

  function reduceVariantsToInfluence(influence, variant, index) {
    return influence.set(index, true);
  }

  function setAllOrNothing(influence) {
    const all = product.getIn([FORM.VARIATIONS, VARIANTS]).reduce(reduceVariantsToInfluence, EMPTY.MAP);

    function reduceAttributes(result, item) {
      return getSize(influence.get(item)) > 0
        ? result.set(item, all)
        : result.set(item, EMPTY.MAP);
    }

    return INFLUENCE_ATTRIBUTES[ETSY].reduce(reduceAttributes, Map({ [ALL]: getSize(all) > 1 }));
  }

  const influence = product.getIn([FORM.VARIATIONS, INFLUENCE]);
  const variants = product.getIn([FORM.VARIATIONS, VARIANTS]);
  const variantsSize = getSize(variants);

  if (variantsSize === 1) {
    return setDefaultValuesToCombinations(
      setAllInfluenced(
        influence.getIn([attribute, variantIndex])
          ? product.get(FORM.VARIATIONS).deleteIn([INFLUENCE, attribute, variantIndex])
          : product.get(FORM.VARIATIONS).setIn([INFLUENCE, attribute, variantIndex], true)
      )
    );
  }

  if (influence.get(ALL)) {
    if (getSize(influence.get(attribute)) > 0) {
      if (isOnlyInfluence({ attribute, influence })) {
        return updateCombinationsWithPreferredValues(
          setAllInfluenced(
            product.get(FORM.VARIATIONS).set(INFLUENCE, influence.deleteIn([attribute, variantIndex]))
          ),
        );
      }

      return setDefaultValuesToCombinations(
        setAllInfluenced(product.get(FORM.VARIATIONS).set(INFLUENCE, influence.set(attribute, EMPTY.MAP)))
      );
    }

    return setDefaultValuesToCombinations(
      setAllInfluenced(
        product
          .get(FORM.VARIATIONS)
          .set(INFLUENCE, influence.set(attribute, variants.reduce(reduceVariantsToInfluence, EMPTY.MAP)))
      )
    );
  }

  if (influence.getIn([attribute, variantIndex])) {
    return setDefaultValuesToCombinations(product.get(FORM.VARIATIONS).deleteIn([INFLUENCE, attribute, variantIndex]));
  }

  if (getSize(influence.get(attribute))) {
    return product.get(FORM.VARIATIONS).set(INFLUENCE, setAllOrNothing(influence));
  }

  return setDefaultValuesToCombinations(product.get(FORM.VARIATIONS).setIn([INFLUENCE, attribute, variantIndex], true));
}
